import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  fetchTappingPoints,
  clearTappingPoints,
  addEditTappingPoint,
  deleteTappingPoint,
} from "./../../actions/tappingPointsActions";
import { uploadImage, clearMedia } from "./../../actions/mediaActions";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import ImageContainerPreview from "../Common/ImageContainerPreview";

const AVATAR_BG = '/img/avatar_man_large.webp';

const AddEditTappingPoints = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tapping_points = useSelector((state) => state.tapping_points.tapping_points);
  const image = useSelector((state) => state.media.image);
  const isFetching = useSelector((state) => state.tapping_points.isLoading);

  const [state, setState] = useState({
    tapping_point_name: "",
    tapping_point_image_url: "",
    tapping_point_is_default: 0,
    isSetToEdit: false,
  });

  // Fetch data when editing
  useEffect(() => {
    if (!isEdit || isFetching) return;

    const fetchData = async () => {
      try {
        dispatch(fetchTappingPoints());
      } catch (error) {
        console.error("Error fetching tapping points:", error);
      }
    };

    fetchData();
  }, [dispatch, isEdit, isFetching]);

  // Set tapping point for editing when data is loaded
  useEffect(() => {
    if (isEdit && tapping_points) {
      const currentPoint = tapping_points.find(
        point => point.tapping_point_id === Number(selectedId)
      );
      if (currentPoint) {
        setState({
          ...currentPoint,
          isSetToEdit: true,
        });
      } else {
        Swal.fire({
          icon: "warning",
          text: "Error finding selected Tapping Point",
        });
      }
    }
  }, [isEdit, tapping_points, selectedId]);

  // Update image URL when image is uploaded
  useEffect(() => {
    if (image) {
      setState(prev => ({
        ...prev,
        tapping_point_image_url: image
      }));
    }
  }, [image]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      dispatch(uploadImage(file));
    }
  };

  const handleRemoveImage = () => {
    setState(prev => ({
      ...prev,
      tapping_point_image_url: ""
    }));
  };

  const submit = useCallback(() => {
    if (!state.tapping_point_name) {
      Swal.fire("Error", "Please enter tapping point name", "error");
      return;
    }

    try {
      const tappingPointData = {
        tapping_point_name: state.tapping_point_name,
        tapping_point_image_url: state.tapping_point_image_url,
        tapping_point_is_default: state.tapping_point_is_default,
        ...(isEdit && { tapping_point_id: selectedId }),
      };

      dispatch(addEditTappingPoint(JSON.stringify(tappingPointData), () => navigate('/tapping-points')));
    } catch (error) {
      console.error("Error saving tapping point:", error);
      Swal.fire("Error", "Failed to save tapping point", "error");
    }
  }, [state, isEdit, selectedId, dispatch, navigate]);

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteTappingPoint(selectedId, () => navigate("/tapping-points")));
      }
    });
  };

  useImperativeHandle(ref, () => ({
    submit
  }), [submit]);

  if (!tapping_points && isEdit) {
    return <LoadingBackdrop open={true} />;
  }

  return (
    <Box component="form" autoComplete="off" sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
        <Grid sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Tapping Point Information</Typography>
          <TextField
            fullWidth
            label="Tapping Point Name"
            value={state.tapping_point_name}
            onChange={(e) => setState(prev => ({ ...prev, tapping_point_name: e.target.value }))}
            disabled={!!state.tapping_point_is_default}
            required
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Image</Typography>
          <Box sx={{ maxWidth: 300 }}>
            <ImageContainerPreview
              title="Tapping Point Image"
              image={state.tapping_point_image_url}
              name="tapping_point_image"
              handleRemove={handleRemoveImage}
              handleImageUpload={handleImageUpload}
              showCopyUrlButton={true}
              sx={{
                height: 'auto',
                minHeight: 200,
                backgroundColor: 'primary.dark',
                backgroundImage: 'none',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                '&:hover': {
                  backgroundImage: `url(${AVATAR_BG})`
                },
                '& img': {
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>
      {isEdit && !state.tapping_point_is_default && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
            variant="contained"
          >
            Delete Tapping Point
          </Button>
        </Box>
      )}
      <LoadingBackdrop open={isFetching || false} />
    </Box>
  );
});

AddEditTappingPoints.displayName = 'AddEditTappingPoints';

export default AddEditTappingPoints;
